import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtMoney, fmtValue } from "@/lib/api";

export const QUOTE_STATUS = {
  bozza: ["Bozza", "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"],
  inviato: ["Inviato", "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]"],
  accettato: ["Accettato", "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"],
  rifiutato: ["Rifiutato", "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]"],
  convertito: ["In contratto", "bg-[#0D2B3A] text-white border-[#0D2B3A]"],
  annullato: ["Contratto annullato", "bg-[#F1F5F9] text-[#475569] border-[#CBD5E1] line-through"],
};

export const QuoteBadge = ({ status, testId }) => {
  const [l, cls] = QUOTE_STATUS[status] || [status, ""];
  return <span data-testid={testId} className={`inline-flex text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full border ${cls}`}>{l}</span>;
};

export default function Quotes() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/quotes").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  const list = useMemo(() => (rows || []).filter((r) => (!filter || r.status === filter) && (!q || `${r.number} ${r.data?.cliente_nome || ""} ${r.evento_tipo}`.toLowerCase().includes(q.toLowerCase()))), [rows, filter, q]);
  const create = async () => {
    try {
      const r = await api.post("/quotes");
      nav(`/preventivi/${r.data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#7B929C]">Offerte ai clienti</div>
          <h1 className="font-serif-it text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-2">Preventivi</h1>
        </div>
        <Button data-testid="new-quote-button" onClick={create} className="rounded-full h-11 px-6"><Plus size={16} className="mr-2" /> Nuovo preventivo</Button>
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {[["", "Tutti"], ...Object.entries(QUOTE_STATUS).map(([k, v]) => [k, v[0]])].map(([k, l]) => (
            <button key={k || "all"} data-testid={`quote-filter-${k || "all"}`} onClick={() => setFilter(k)}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${filter === k ? "bg-[#0D2B3A] text-white border-[#0D2B3A]" : "bg-white border-[#D4E6EC] hover:border-[#0B6E8F]"}`}>{l}</button>
          ))}
        </div>
        <div className="relative md:w-80">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B929C]" />
          <input data-testid="quotes-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente o numero…"
            className="w-full rounded-full border border-[#D4E6EC] bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B6E8F]/30" />
        </div>
      </div>
      <div className="rounded-xl border border-[#D4E6EC] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="quotes-table">
          <thead className="text-left text-[11px] uppercase tracking-wider text-[#7B929C] border-b border-[#D4E6EC]">
            <tr><th className="p-4">N.</th><th className="p-4">Cliente</th><th className="p-4">Evento</th><th className="p-4 text-right">Totale</th><th className="p-4">Stato</th></tr>
          </thead>
          <tbody>
            {rows && list.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-[#7B929C]" data-testid="quotes-empty">Nessun preventivo.</td></tr>}
            {list.map((r) => (
              <tr key={r.id} data-testid={`quote-row-${r.number}`} onClick={() => nav(`/preventivi/${r.id}`)} className="border-b border-[#EAF5F7] hover:bg-[#F5FAFB] cursor-pointer">
                <td className="p-4 font-mono-it text-xs">{r.number}</td>
                <td className="p-4 font-medium">{r.data?.cliente_nome || <span className="text-[#A8C6CF]">—</span>}</td>
                <td className="p-4">{r.evento_tipo} <span className="text-[#7B929C]">{fmtValue({ type: "date" }, r.data?.evento_data)}</span></td>
                <td className="p-4 text-right">{fmtMoney(r.totals?.totale || 0)}</td>
                <td className="p-4"><QuoteBadge status={r.status} testId={`quote-status-${r.number}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
